
import React, { useState, useRef } from 'react';
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import { Typography, CircularProgress } from "@mui/material";
import Card from "@mui/material/Card";
import MDTypography from "components/MDTypography";

// -----------------------------------------------------------
// 🚀 MAIN COMPONENT
// -----------------------------------------------------------

function AudienceGrowthChart({
    title,
    datasets,
    previousdatasets, // ✅ Accept Prop
    isLoading,
    entitlements,
    viewMode,
    onViewModeChange,
    profileColorMap // ✅ Global Color Map
}) {
    // Define base colors for Gain/Loss
    const COLOR_GAIN_BAR = "rgba(75, 192, 192, 0.8)"; // Positive (Teal)
    const COLOR_LOSS_BAR = "rgba(255, 99, 132, 0.8)"; // Negative (Red)


    // ---------------------------------------------------------------------
    // RENDER LOADER
    // ---------------------------------------------------------------------
    const renderLoader = () => (
        <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
                borderRadius: "0.75rem",
            }}
        >
            <CircularProgress size={32} thickness={4} color="accent" />
        </MDBox>
    );

    // Safely fallback
    const safeDatasets = datasets || [];
    const safePreviousDatasets = previousdatasets || []; // ✅ Define Variable

    // Align labels (Assume all datasets share same dates for now, or take first)
    const chartLabels = safeDatasets.length > 0 ? safeDatasets[0].data.map((item) => item.date) : [];

    // 🚀 Title Logic (Mirrored from AudienceCountChart but for "Change")
    const platformSet = new Set(safeDatasets.map((profile) => profile.platform));
    let derivedTitle = '';

    if (platformSet.size === 1) {
        const platform = platformSet.values().next().value;
        derivedTitle = platform === "YouTube" ? "Subscribers Change Over Time" : "Followers Change Over Time";
    } else if (platformSet.size > 1) {
        derivedTitle = [...platformSet].includes("YouTube") ? "Subscribers Change Over Time" : "Followers Change Over Time";
    } else {
        derivedTitle = "Followers Change Over Time";
    }

    // ⬇️=========================================
    // ⬇️ CALCULATION & DATASET CONSTRUCTION
    // ⬇️=========================================

    // ⬇️=========================================
    // ⬇️ CALCULATION & DATASET CONSTRUCTION
    // ⬇️=========================================

    const processDataset = (inputDatasets) => {
        const resultDatasets = [];

        inputDatasets.forEach((profile, index) => {
            // 1. Prepare Arrays
            const netData = [];
            const gainData = [];
            const lossData = [];
            const predictionFlags = [];

            profile.data.forEach(item => {
                // ✅ Deep Access as requested (no global hoisting)
                const metrics = item.profileMetrics || item.pageMetrics || item.channelMetrics || {};

                // Standardized keys: 'followers_gain' and 'followers_lost'
                const gain = metrics.followers_gain !== undefined ? metrics.followers_gain :
                    (metrics.follower_gain !== undefined ? metrics.follower_gain :
                        (metrics.subscribersGained !== undefined ? metrics.subscribersGained : (item.followers_gain || 0)));

                const loss = metrics.followers_lost !== undefined ? metrics.followers_lost :
                    (metrics.subscribersLost !== undefined ? metrics.subscribersLost : (item.followers_lost || 0));

                // Net can be pre-calculated or derived
                const rawNet = metrics.net_followers !== undefined ? metrics.net_followers : (item.net_followers);
                const net = rawNet !== undefined ? rawNet : (gain - loss);

                // -- Push Data
                netData.push(net);
                gainData.push(gain);
                lossData.push(-1 * Math.abs(loss)); // Negate for "Down" bar
                predictionFlags.push(false);
            });

            // 2. Create Chart.js Datasets

            // A) Net Change (LINE) - Overlay
            resultDatasets.push({
                type: 'line',
                label: profile.label, // ✅ Use just the name for Net Change
                data: netData,
                isPredictedFlags: predictionFlags,
                // 🚀 Removed manual colors to use profileColorMap via DefaultLineChart
                borderWidth: 2,
                pointRadius: 3,
                tension: 0.4,
                order: 1, // Top
                profileId: profile.profileId,
                platform: profile.platform,
                profilePictureUrl: profile.profilePictureUrl, // ✅ Fix Icon
                metricName: "Net Change"
            });

            // B) New Follows (BAR) - Up
            resultDatasets.push({
                type: 'bar',
                label: `Gain (${profile.label})`,
                data: gainData,
                backgroundColor: COLOR_GAIN_BAR,
                borderColor: COLOR_GAIN_BAR,
                barThickness: 'flex', // Auto-size
                maxBarThickness: 20,
                order: 2,
                profileId: profile.profileId,
                stack: `stack-${profile.profileId}`, // Stack Gain/Loss per profile
                metricName: "New Follows",
                skipMetricCard: true, // ✅ Don't show card for this dataset
                skipLegend: true // ✅ Don't show in legend
            });

            // C) Followers Lost (BAR) - Down
            resultDatasets.push({
                type: 'bar',
                label: `Loss (${profile.label})`,
                data: lossData,
                backgroundColor: COLOR_LOSS_BAR,
                borderColor: COLOR_LOSS_BAR,
                barThickness: 'flex',
                maxBarThickness: 20,
                order: 2,
                profileId: profile.profileId,
                stack: `stack-${profile.profileId}`, // Stack with Gain
                metricName: "Followers Lost",
                skipMetricCard: true, // ✅ Don't show card for this dataset
                skipLegend: true // ✅ Don't show in legend
            });
        });

        return resultDatasets;
    };

    const chartDatasets = processDataset(safeDatasets);
    const previousChartDatasets = processDataset(safePreviousDatasets);

    // ✅ Construct Chart Data Object
    const computedChartData = {
        labels: chartLabels,
        datasets: chartDatasets,
        previousdatasets: previousChartDatasets // ✅ Pass Previous Data for Comparison
    };

    // -----------------------------------------------------------
    // PERSISTENCE & LOADING
    // -----------------------------------------------------------
    const lastChartDataRef = useRef(null);
    if (safeDatasets.length > 0) {
        lastChartDataRef.current = computedChartData;
    }

    let dataToRender = computedChartData;
    if (isLoading && safeDatasets.length === 0 && lastChartDataRef.current) {
        dataToRender = lastChartDataRef.current;
    }

    const hasData = (dataToRender.datasets && dataToRender.datasets.length > 0);

    // -----------------------------------------------------------
    // CONFIGURATION OVERRIDES
    // -----------------------------------------------------------

    const overrideOptions = {
        interaction: {
            mode: 'index',
            intersect: false,
        },
        scales: {
            y: {
                title: {
                    display: true,
                    text: derivedTitle.replace(" Over Time", "")
                },
                grid: {
                    drawOnChartArea: true,
                    color: 'rgba(0,0,0,0.1)', // ✅ Uniform grid color (no dark 0-line)
                    lineWidth: 1,
                },
                ticks: {
                    callback: (val) => val.toLocaleString()
                }
            },
            x: {
                stacked: true, // Enable stacking for the bars (using stack groups)
            }
        },
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                // 🚀 ENABLE CUSTOM TOOLTIP (Disable DefaultLineChart trend logic)
                enabled: true,
                callbacks: {
                    // Start fresh: Clear Title and Body sections inherited from defaults
                    title: (context) => {
                        const date = context[0].label;
                        return date; // Just show Date
                    },
                    afterTitle: () => "",
                    beforeBody: () => "",
                    afterBody: () => "", // ✅ This kills the "Previous Value: 0" / "Increase %" text
                    footer: () => "",

                    label: function (context) {
                        let label = context.dataset.label || '';
                        let value = context.parsed.y;
                        const dataIndex = context.dataIndex;
                        const datasets = context.chart.data.datasets;

                        // ✅ For Net Change Line: Show detailed breakdown
                        if (context.dataset.metricName === "Net Change") {
                            // Return simple string (User requested to remove the Gained/Lost details from this specific block)
                            return `Net Change: ${value > 0 ? '+' : ''}${value.toLocaleString()}`;
                        }

                        // ✅ Simplify other dataset labels (though usually hidden from tooltip by interaction mode, or if showing all)
                        if (label.includes("Gain")) {
                            return `Gain: ${value.toLocaleString()}`;
                        }
                        if (label.includes("Loss") || label.includes("Followers Lost")) {
                            return `Loss: ${Math.abs(value).toLocaleString()}`;
                        }
                        // Default Fallback
                        return `${label}: ${value.toLocaleString()}`;
                    }
                }
            }
        }
    };

    // -----------------------------------------------------------
    // RENDER
    // -----------------------------------------------------------

    const tooltipContent = (
        <div style={{ maxWidth: "350px", textAlign: "left" }}>
            <Typography variant="body2" gutterBottom>
                <strong>Description:</strong>
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
                This chart displays the net daily change in your audience.
                It highlights days of significant growth or loss.
            </Typography>

            <Typography variant="body2" gutterBottom>
                <strong>How to read the chart:</strong>
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
                - <strong>Positive Bars (Up)</strong>: You gained new followers on that day.<br />
                - <strong>Negative Bars (Down)</strong>: You lost followers on that day.<br />
                - <strong>Tall Bars</strong>: Indicates a viral moment or a drop-off event.
            </Typography>

            <Typography variant="body2" gutterBottom>
                <strong>Insights you can gain:</strong>
            </Typography>
            <Typography variant="caption" display="block">
                - Identify which specific posts caused a surge in followers.<br />
                - Spot days with unusual follower loss to investigate causes.<br />
                - Correlate spikes with campaigns or viral content.<br />
                - Measure the immediate impact of your daily activity.
            </Typography>
        </div>
    );

    const gainLossLegend = (
        <MDBox display="flex" justifyContent="center" alignItems="center" pb={2} mt={-1}>
            <MDBox display="flex" alignItems="center" mr={3}>
                <MDBox sx={{ width: 14, height: 14, borderRadius: "2px", backgroundColor: COLOR_GAIN_BAR, mr: 1 }} />
                <MDTypography variant="button" color="text" sx={{ fontWeight: "medium" }}>Gain</MDTypography>
            </MDBox>
            <MDBox display="flex" alignItems="center">
                <MDBox sx={{ width: 14, height: 14, borderRadius: "2px", backgroundColor: COLOR_LOSS_BAR, mr: 1 }} />
                <MDTypography variant="button" color="text" sx={{ fontWeight: "medium" }}>Loss</MDTypography>
            </MDBox>
        </MDBox>
    );


    return (
        <div style={{ position: "relative" }}>
            {isLoading && renderLoader()}

            {hasData ? (
                <DefaultLineChart
                    title={derivedTitle}
                    description={tooltipContent}
                    height="19.125rem"
                    chart={dataToRender}
                    rawData={safeDatasets}
                    entitlements={entitlements}
                    isSnapshot={false}
                    viewMode={viewMode}
                    onViewModeChange={onViewModeChange}
                    overrideOptions={overrideOptions}
                    aggregate={true} // ✅ Sum values for Metric Card (Total Net Change)
                    profileColorMap={profileColorMap} // ✅ Pass Global Colors
                    customFooter={gainLossLegend} // ✅ NEW: Gain/Loss Legend
                />
            ) : (
                <Card sx={{ height: "19.125rem" }}>
                    {!isLoading && (
                        <MDTypography variant="body2" color="text" sx={{ p: 3, textAlign: "center" }}>
                            No detailed growth data available.
                        </MDTypography>
                    )}
                </Card>
            )}
        </div>
    );
}

AudienceGrowthChart.propTypes = {
    title: PropTypes.string,
    datasets: PropTypes.array,
    isLoading: PropTypes.bool,
    entitlements: PropTypes.object,
    viewMode: PropTypes.string,
    onViewModeChange: PropTypes.func,
    profileColorMap: PropTypes.object,
};

export default AudienceGrowthChart;
